/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                       - CUSTOM SLIDER -                       |
|_______________________________________________________________|
*/

(() => {
  if (window.__customSliderInit) return;
  window.__customSliderInit = true;

  const DRAG_THRESHOLD = 6;
  const EDGE_ZONE = 32;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  const stateByCarousel = new WeakMap();

  function maxScrollLeft(track) {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  function getTrackPageMetrics(track) {
    const styles = window.getComputedStyle(track);
    const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
    const pageWidth = Math.max(1, track.clientWidth - paddingLeft - paddingRight);

    return {
      pageLeft: paddingLeft,
      pageRight: track.clientWidth - paddingRight,
      pageWidth,
      pageStart: track.scrollLeft + paddingLeft,
      pageCenter: track.scrollLeft + paddingLeft + pageWidth / 2,
    };
  }

  function leadingIndex(track, slides) {
    const { pageStart } = getTrackPageMetrics(track);

    let best = 0;
    let bestDist = Infinity;

    for (let i = 0; i < slides.length; i++) {
      const d = Math.abs(slides[i].offsetLeft - pageStart);

      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }

    return best;
  }

  function lockSnap(track, where) {
    track.style.scrollSnapType = "none";
    track.dataset.csSnapLocked = where;
  }

  function unlockSnap(track) {
    delete track.dataset.csSnapLocked;
    track.style.scrollSnapType = "";
  }

  function maybeUnlockSnap(track) {
    const locked = track.dataset.csSnapLocked;
    if (!locked) return;

    const max = maxScrollLeft(track);
    const left = track.scrollLeft;

    if (left > EDGE_ZONE && left < max - EDGE_ZONE) {
      unlockSnap(track);
    }
  }

  function waitForScrollEnd(track, targetLeft, signal) {
    return new Promise((resolve) => {
      if (signal?.aborted) {
        resolve();
        return;
      }

      if ("onscrollend" in window) {
        const onEnd = () => {
          track.removeEventListener("scrollend", onEnd);
          resolve();
        };

        track.addEventListener("scrollend", onEnd, { once: true });

        if (signal) {
          signal.addEventListener(
            "abort",
            () => {
              track.removeEventListener("scrollend", onEnd);
              resolve();
            },
            { once: true }
          );
        }

        return;
      }

      let stableFrames = 0;

      const tick = () => {
        if (signal?.aborted) {
          resolve();
          return;
        }

        const diff = Math.abs(track.scrollLeft - targetLeft);
        stableFrames = diff < 1 ? stableFrames + 1 : 0;

        if (stableFrames >= 2) {
          resolve();
          return;
        }

        requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    });
  }

  function updateArrows(track, prevBtn, nextBtn) {
    const max = maxScrollLeft(track);
    const left = track.scrollLeft;

    prevBtn.disabled = left <= 2;
    nextBtn.disabled = left >= max - 2;
  }

  function setupVisibilityPaging(track) {
    const getStep = () => {
      const section = track.closest(".custom-slider-section") || track;
      const raw = window.getComputedStyle(section).getPropertyValue("--cs-step").trim();
      const step = Number.parseInt(raw, 10);

      return Number.isFinite(step) && step > 0 ? step : 1;
    };

    return {
      io: { disconnect() {} },
      getStep,
    };
  }

  function closestIndex(track, slides) {
    const { pageCenter } = getTrackPageMetrics(track);

    let best = 0;
    let bestDist = Infinity;

    for (let i = 0; i < slides.length; i++) {
      const c = slides[i].offsetLeft + slides[i].offsetWidth / 2;
      const d = Math.abs(c - pageCenter);

      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }

    return best;
  }

  function snapToSlide(track, slide, align = "start") {
    if (!slide) return;

    unlockSnap(track);

    const max = maxScrollLeft(track);
    const { pageLeft, pageWidth } = getTrackPageMetrics(track);

    const targetLeftRaw =
      align === "center"
        ? slide.offsetLeft - pageLeft - (pageWidth - slide.clientWidth) / 2
        : slide.offsetLeft - pageLeft;

    const targetLeft = clamp(targetLeftRaw, 0, max);

    track.scrollTo({
      left: targetLeft,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }

  function cancelOngoingSnap(track, snapAbortRef) {
    if (snapAbortRef.current) {
      try {
        snapAbortRef.current.abort();
      } catch (_) {}

      snapAbortRef.current = null;
    }

    track.scrollTo({ left: track.scrollLeft, behavior: "auto" });
  }

  function setupMouseDrag(track, slides, signal, snapAbortRef) {
    let pointerId = null;
    let startX = 0;
    let startLeft = 0;

    let moved = false;
    let dragging = false;

    let startTime = 0;
    let previous = 0;
    let velocity = 0;
    let distanceTravelled = 0;

    let startIndex = 0;

    track.addEventListener("dragstart", (e) => e.preventDefault(), { signal });

    track.addEventListener(
      "click",
      (e) => {
        if (!moved) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      },
      { capture: true, signal }
    );

    const beginDragMode = (e) => {
      dragging = true;
      track.setPointerCapture(e.pointerId);

      track.style.scrollSnapType = "none";
      track.style.scrollBehavior = "auto";
      track.style.cursor = "grabbing";
      document.documentElement.style.userSelect = "none";
    };

    const endDragMode = () => {
      dragging = false;
      document.documentElement.style.userSelect = "";
      track.style.cursor = "";
      track.style.scrollBehavior = "";
    };

    const finishSnap = async (targetLeft, { lockEdge } = {}) => {
      cancelOngoingSnap(track, snapAbortRef);

      const ac = new AbortController();
      snapAbortRef.current = ac;

      track.style.scrollSnapType = "none";
      track.style.scrollBehavior = "auto";

      track.scrollTo({
        left: targetLeft,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });

      await waitForScrollEnd(track, targetLeft, ac.signal);

      if (ac.signal.aborted) return;

      if (lockEdge === "start") {
        lockSnap(track, "start");
      } else if (lockEdge === "end") {
        lockSnap(track, "end");
      } else {
        unlockSnap(track);
      }

      snapAbortRef.current = null;
    };

    track.addEventListener(
      "pointerdown",
      (e) => {
        if (e.pointerType !== "mouse") return;
        if (e.button !== 0) return;

        cancelOngoingSnap(track, snapAbortRef);

        pointerId = e.pointerId;
        moved = false;
        dragging = false;

        startX = e.clientX;
        startLeft = track.scrollLeft;

        startTime = performance.now();
        previous = startX;
        velocity = 0;
        distanceTravelled = 0;

        startIndex = closestIndex(track, slides);
      },
      { signal }
    );

    track.addEventListener(
      "pointermove",
      (e) => {
        if (e.pointerType !== "mouse") return;
        if (pointerId == null || e.pointerId !== pointerId) return;

        const current = e.clientX;
        const initialDelta = startX - current;

        if (!initialDelta) return;

        if (!moved && Math.abs(initialDelta) >= DRAG_THRESHOLD) {
          moved = true;
          beginDragMode(e);
        }

        if (!dragging) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const max = maxScrollLeft(track);
        const nextLeft = clamp(startLeft + initialDelta, 0, max);

        track.scrollLeft = nextLeft;

        const delta = previous - current;
        const timeDelta = performance.now() - startTime;

        velocity = timeDelta > 0 ? Math.round((delta / timeDelta) * 1000) : 0;
        previous = current;
        distanceTravelled += Math.abs(delta);
      },
      { signal }
    );

    const onPointerUp = async (e) => {
      if (pointerId == null || e.pointerId !== pointerId) return;

      pointerId = null;

      if (dragging) {
        try {
          track.releasePointerCapture(e.pointerId);
        } catch (_) {}
      }

      endDragMode();

      if (!moved) return;

      const max = maxScrollLeft(track);
      const left = track.scrollLeft;

      const nearStart = left <= EDGE_ZONE;
      const nearEnd = left >= max - EDGE_ZONE;

      if (nearStart) {
        await finishSnap(0, { lockEdge: "start" });
        moved = false;
        return;
      }

      if (nearEnd) {
        await finishSnap(max, { lockEdge: "end" });
        moved = false;
        return;
      }

      const direction = Math.sign(velocity);
      const next = closestIndex(track, slides);

      const modifier =
        startIndex !== next || Math.abs(velocity) < 10 || distanceTravelled < 10
          ? 0
          : direction;

      const newIndex = clamp(next + modifier, 0, slides.length - 1);
      const slide = slides[newIndex];

      if (!slide) {
        moved = false;
        unlockSnap(track);
        return;
      }

      const { pageLeft, pageWidth } = getTrackPageMetrics(track);
      const targetLeftRaw =
        slide.offsetLeft - pageLeft - (pageWidth - slide.clientWidth) / 2;
      const targetLeft = clamp(targetLeftRaw, 0, max);

      await finishSnap(targetLeft);
      moved = false;
    };

    track.addEventListener("pointerup", onPointerUp, { signal });
    track.addEventListener("pointercancel", onPointerUp, { signal });
    track.addEventListener("pointercapturelost", onPointerUp, { signal });
  }

  function initCarousel(carousel) {
    if (!carousel) return;

    const section = carousel.closest(".custom-slider-section");

    if (section) {
      section.classList.add("custom-slider-loaded");
    }

    if (section && section.dataset.csHasTitle === "1" && section.dataset.csTitleId) {
      carousel.setAttribute("aria-labelledby", section.dataset.csTitleId);
    }

    const track = carousel.querySelector("[data-custom-slider-track]");
    const prevBtn = carousel.querySelector("[data-custom-slider-prev]");
    const nextBtn = carousel.querySelector("[data-custom-slider-next]");

    if (!track || !prevBtn || !nextBtn) return;

    const slides = Array.from(carousel.querySelectorAll("[data-custom-slider-slide]"));

    if (slides.length <= 1) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }

    const prevState = stateByCarousel.get(carousel);

    if (prevState?.track === track && prevState.slideCount === slides.length) {
      updateArrows(track, prevBtn, nextBtn);
      return;
    }

    if (prevState) {
      try {
        prevState.abort.abort();
      } catch (_) {}

      try {
        prevState.ro.disconnect();
      } catch (_) {}

      try {
        prevState.io.disconnect();
      } catch (_) {}
    }

    const abort = new AbortController();
    const { signal } = abort;

    const snapAbortRef = { current: null };
    const { io, getStep } = setupVisibilityPaging(track, slides);

    const onPrev = () => {
      cancelOngoingSnap(track, snapAbortRef);
      unlockSnap(track);

      const idx = leadingIndex(track, slides);
      const step = getStep();

      snapToSlide(track, slides[clamp(idx - step, 0, slides.length - 1)], "start");
    };

    const onNext = () => {
      cancelOngoingSnap(track, snapAbortRef);
      unlockSnap(track);

      const idx = leadingIndex(track, slides);
      const step = getStep();

      snapToSlide(track, slides[clamp(idx + step, 0, slides.length - 1)], "start");
    };

    prevBtn.addEventListener("click", onPrev, { signal });
    nextBtn.addEventListener("click", onNext, { signal });

    let raf = 0;

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);

      raf = requestAnimationFrame(() => {
        updateArrows(track, prevBtn, nextBtn);
        maybeUnlockSnap(track);
      });
    };

    track.addEventListener("scroll", onScroll, { passive: true, signal });

    const ro = new ResizeObserver(() => {
      updateArrows(track, prevBtn, nextBtn);
      maybeUnlockSnap(track);
    });

    ro.observe(track);

    setupMouseDrag(track, slides, signal, snapAbortRef);

    stateByCarousel.set(carousel, {
      track,
      slideCount: slides.length,
      abort,
      ro,
      io,
    });

    updateArrows(track, prevBtn, nextBtn);
  }

  function initAllCustomSliders() {
    document
      .querySelectorAll(".custom-slider-section [data-custom-slider-carousel]")
      .forEach(initCarousel);
  }

  document.addEventListener("DOMContentLoaded", initAllCustomSliders);
  document.addEventListener("shopify:section:load", initAllCustomSliders);
  document.addEventListener("custom-slider:contentchange", initAllCustomSliders);

  document.addEventListener("rebuy.ready", initAllCustomSliders);
  document.addEventListener("rebuy.productsChange", initAllCustomSliders);
})();

/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                    - RECENTLY VIEWED TRACKER -                 |
|________________________________________________________________|
*/

(() => {
  if (window.__csRecentlyViewedTracker) return;
  window.__csRecentlyViewedTracker = true;

  const STORAGE_KEY = "recently_viewed_products";
  const MAX_ITEMS = 20;
  const COOKIE_KEY = "recently_viewed_products_min";
  const COOKIE_MAX = 10;

  function safeJsonParse(str) {
    try {
      return JSON.parse(str);
    } catch (_) {
      return null;
    }
  }

  function imgWidth(src, width, fallback) {
    const s = src || fallback || "";

    if (!s) return "";

    try {
      const u = new URL(s, window.location.origin);
      u.searchParams.set("width", String(width));
      return u.toString();
    } catch (_) {
      return s.includes("?") ? `${s}&width=${width}` : `${s}?width=${width}`;
    }
  }

  function getMainVariantPicker() {
    return (
      document.querySelector('variant-picker[data-template-product-match="true"]') ||
      document.querySelector("variant-picker")
    );
  }

  function readSelectedVariantFromPicker() {
    const picker = getMainVariantPicker();

    if (!picker) return null;

    const jsonEl = picker.querySelector('script[type="application/json"]');

    if (!jsonEl?.textContent) return null;

    return safeJsonParse(jsonEl.textContent);
  }

  function getVariantIdFromUrl() {
    const v = new URL(window.location.href).searchParams.get("variant");
    return v ? String(v) : null;
  }

  function saveItem(item) {
    if (!item || !item.handle || !item.variant_id) return;

    try {
      const prev = safeJsonParse(localStorage.getItem(STORAGE_KEY)) || [];
      const next = Array.isArray(prev)
        ? prev.filter((x) => x && x.handle !== item.handle)
        : [];

      next.unshift(item);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(next.slice(0, MAX_ITEMS)));
    } catch (_) {}

    try {
      const miniItem = { h: item.handle, v: item.variant_id };
      let mini = [];

      const m = document.cookie.match(
        `(^|;)\\s*${COOKIE_KEY}\\s*=\\s*([^;]+)`
      );

      if (m) {
        mini = safeJsonParse(decodeURIComponent(m[2])) || [];
      }

      mini = Array.isArray(mini)
        ? mini.filter((x) => x && x.h !== miniItem.h)
        : [];

      mini.unshift(miniItem);
      mini = mini.slice(0, COOKIE_MAX);

      document.cookie = `${COOKIE_KEY}=${encodeURIComponent(
        JSON.stringify(mini)
      )}; path=/; max-age=${60 * 60 * 24 * 7}`;
    } catch (_) {}
  }

  function initOnPdp() {
    const dataEl = document.querySelector("script[data-cs-rv-pdp]");

    if (!dataEl?.textContent) return;

    const data = safeJsonParse(dataEl.textContent);

    if (!data?.handle || !Array.isArray(data.variants)) return;

    const byId = Object.fromEntries(data.variants.map((v) => [String(v.id), v]));

    const comingSoonIds = new Set(
      Array.isArray(data.coming_soon_variant_ids)
        ? data.coming_soon_variant_ids.map((id) => String(id))
        : []
    );

    function buildData(variantId) {
      const v = byId[String(variantId)];

      if (!v) return null;

      const src =
        (v.featured_image && (v.featured_image.src || v.featured_image.url)) ||
        v.featured_image ||
        (v.image && (v.image.src || v.image)) ||
        null;

      const price = Number(v.price);
      const compare = Number(v.compare_at_price);
      const isComingSoon = comingSoonIds.has(String(v.id));

      return {
        handle: data.handle,
        title: data.title || "",
        subtitle: data.subtitle || "",
        variant_id: String(v.id),
        option1: v.option1 || "",
        option2: v.option2 || "",
        available: v.available !== false,
        coming_soon: isComingSoon,
        price: Number.isFinite(price) ? price : null,
        compare_at_price:
          Number.isFinite(compare) && Number.isFinite(price) && compare > price
            ? compare
            : null,
        image: imgWidth(src, 600, data.featured_image),
        ts: Date.now(),
      };
    }

    function sync() {
      const urlId = getVariantIdFromUrl();
      const pickerV = readSelectedVariantFromPicker();

      const id =
        urlId ||
        (pickerV?.id ? String(pickerV.id) : null) ||
        (data.variants[0] ? String(data.variants[0].id) : null);

      if (!id) return;

      const item = buildData(id);

      if (item) {
        saveItem(item);
      }
    }

    sync();

    document.addEventListener(
      "change",
      (e) => {
        const t = e.target;

        if (!(t instanceof Element)) return;

        if (t.closest("variant-picker")) {
          setTimeout(sync, 60);
        }
      },
      true
    );

    const hookHistory = (method) => {
      const original = history[method];

      if (typeof original !== "function") return;

      history[method] = function (...args) {
        const ret = original.apply(this, args);
        window.dispatchEvent(new Event("cs:locationchange"));
        return ret;
      };
    };

    if (!window.__csHistoryHooked) {
      window.__csHistoryHooked = true;
      hookHistory("pushState");
      hookHistory("replaceState");
    }

    window.addEventListener("popstate", sync);
    window.addEventListener("cs:locationchange", sync);
  }

  document.addEventListener("DOMContentLoaded", initOnPdp);
})();

/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                   - RECENTLY VIEWED RENDERER -                 |
|________________________________________________________________|
*/

(() => {
  if (window.__csRecentlyViewedRender) return;
  window.__csRecentlyViewedRender = true;

  function safeJsonParse(str) {
    try {
      return JSON.parse(str);
    } catch (_) {
      return null;
    }
  }

  function formatMoney(cents) {
    const n = Number(cents);

    if (!Number.isFinite(n)) return "";

    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      const fmt = window.Shopify.money_format || "{{amount}}";
      return window.Shopify.formatMoney(n, fmt);
    }

    const currency =
      window.Shopify?.currency?.active ||
      document.querySelector('meta[property="og:price:currency"]')?.content ||
      "EUR";

    const locale = document.documentElement.lang || "de-DE";

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(n / 100);
  }

  function hydrateWrap(wrap) {
    const section = wrap.querySelector(".custom-slider-section");

    const key =
      section?.dataset.csRvKey ||
      wrap.dataset.csRvKey ||
      "recently_viewed_products";

    const max = Math.max(
      1,
      Number.parseInt(section?.dataset.csRvMax || wrap.dataset.csRvMax || "10", 10)
    );

    const isDesignMode =
      section?.dataset.csDesignMode === "1" || wrap.dataset.csDesignMode === "1";

    if (isDesignMode) {
      wrap.dataset.csDesignMode = "1";
    }

    let list = safeJsonParse(localStorage.getItem(key)) || [];

    if (!Array.isArray(list)) {
      list = [];
    }

    list = list.filter((x) => x && x.handle && x.title && x.image);
    list = list.slice(0, max);

    const track = wrap.querySelector("[data-cs-rv-track]");
    const tpl = wrap.querySelector("template[data-cs-rv-template]");
    const placeholder = wrap.querySelector("[data-cs-rv-empty]");

    if (!track || !tpl || !section) return;

    if (list.length === 0) {
      if (isDesignMode) {
        wrap.classList.add("custom-slider-ready");
        section.classList.add("custom-slider-loaded");

        if (placeholder) {
          placeholder.style.display = "block";
        }
      } else {
        wrap.classList.add("custom-slider-empty");
      }

      return;
    }

    if (placeholder) {
      placeholder.style.display = "none";
    }

    track.innerHTML = "";

    list.forEach((item, idx) => {
      const node = tpl.content.firstElementChild.cloneNode(true);

      const a = node.querySelector("a.cs-card");
      const img = node.querySelector("img.cs-image");
      const title = node.querySelector(".cs-product-title");
      const subtitle = node.querySelector(".cs-product-subtitle");
      const priceCur = node.querySelector(".cs-price-current");
      const priceCmp = node.querySelector(".cs-price-compare");
      const opt1 = node.querySelector(".cs-opt1");
      const opt2 = node.querySelector(".cs-opt2");
      const badges = node.querySelector("[data-cs-rv-badges]");
      const comingSoonBadge = node.querySelector("[data-cs-rv-badge-coming-soon]");
      const soldOutBadge = node.querySelector("[data-cs-rv-badge-sold-out]");
      const saleBadge = node.querySelector("[data-cs-rv-badge-sale]");

      const href = `/products/${item.handle}${
        item.variant_id ? `?variant=${encodeURIComponent(item.variant_id)}` : ""
      }`;

      if (a) {
        a.href = href;
        a.setAttribute("aria-label", item.title || "");
      }

      if (img) {
        img.src = item.image;
        img.alt = item.title || "";
        img.loading = idx < 2 ? "eager" : "lazy";
        img.fetchPriority = idx < 2 ? "high" : "auto";
      }

      if (title) {
        title.textContent = item.title || "";
      }

      if (subtitle) {
        subtitle.textContent = item.subtitle || "";
      }

      if (priceCur) {
        priceCur.textContent = formatMoney(item.price);
      }

      if (priceCmp) {
        if (
          item.compare_at_price &&
          Number(item.compare_at_price) > Number(item.price)
        ) {
          priceCmp.style.display = "";
          priceCmp.textContent = formatMoney(item.compare_at_price);
        } else {
          priceCmp.style.display = "none";
          priceCmp.textContent = "";
        }
      }

      if (opt1) {
        opt1.textContent = item.option1 || "";
      }

      if (opt2) {
        opt2.textContent = item.option2 || "";
      }

      if (badges && comingSoonBadge && soldOutBadge && saleBadge) {
        const showBadges = badges.dataset.csRvShowBadges === "true";

        const isComingSoon =
          item.coming_soon === true ||
          item.coming_soon === "true" ||
          item.coming_soon === 1 ||
          item.coming_soon === "1";

        const isSoldOut = item.available === false && !isComingSoon;

        const isOnSale =
          item.compare_at_price &&
          Number(item.compare_at_price) > Number(item.price);

        badges.hidden = !(isComingSoon || (showBadges && (isSoldOut || isOnSale)));
        comingSoonBadge.hidden = !isComingSoon;
        soldOutBadge.hidden = !(showBadges && isSoldOut);
        saleBadge.hidden = !(showBadges && !isComingSoon && !isSoldOut && isOnSale);
      }

      track.appendChild(node);
    });

    wrap.classList.add("custom-slider-ready");
    wrap.classList.remove("custom-slider-empty");
    section.classList.add("custom-slider-loaded");

    document.dispatchEvent(new Event("custom-slider:contentchange"));
  }

  function hydrateAll() {
    document
      .querySelectorAll('.custom-slider-wrap[data-cs-feed-mode="recently_viewed"]')
      .forEach(hydrateWrap);
  }

  document.addEventListener("DOMContentLoaded", hydrateAll);
  document.addEventListener("shopify:section:load", hydrateAll);
})();

/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                 - SHOPIFY RECOMMENDATIONS FEED -               |
|________________________________________________________________|
*/

(() => {
  if (window.__customSliderRecommendationsFeedInit) return;
  window.__customSliderRecommendationsFeedInit = true;

  const observed = new WeakSet();

  function updateWrap(wrap) {
    const pr = wrap.querySelector("product-recommendations");

    if (!pr) return;

    if (pr.classList.contains("hidden")) {
      wrap.classList.add("custom-slider-empty");
      return;
    }

    const marker = pr.querySelector("[data-cs-has-items]");

    if (!marker) return;

    const has = marker.getAttribute("data-cs-has-items");

    if (has === "false") {
      wrap.classList.add("custom-slider-empty");
      return;
    }

    if (has === "true") {
      wrap.classList.remove("custom-slider-empty");
      wrap.querySelector(".custom-slider-section")?.classList.add("custom-slider-loaded");

      document.dispatchEvent(new Event("custom-slider:contentchange"));
    }
  }

  function observeWrap(wrap) {
    if (observed.has(wrap)) return;

    observed.add(wrap);

    const pr = wrap.querySelector("product-recommendations");

    if (!pr) return;

    const mo = new MutationObserver(() => updateWrap(wrap));

    mo.observe(pr, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "data-recommendations-performed"],
    });

    updateWrap(wrap);
  }

  function init() {
    document
      .querySelectorAll('.custom-slider-wrap[data-cs-feed-mode="recommendations"]')
      .forEach(observeWrap);
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();